import CancelIcon from "@mui/icons-material/Cancel";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import React, {useEffect, useRef, useState} from "react";

interface ElementChipProps {
    text: string;
    startIcon?: React.ReactNode;
    borderColor?: string;
    backgroundColor?: string;
    tooltipContent?: React.ReactNode;
    onClick?: () => void;
    onDelete?: () => void;
}

export function ElementChip({
    text,
    startIcon,
    borderColor,
    backgroundColor,
    tooltipContent,
    onClick,
    onDelete,
}: ElementChipProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const element = textRef.current;
        if (!element) return;

        const checkTruncation = () => {
            if (!isExpanded) {
                setIsTruncated(element.scrollWidth > element.clientWidth);
            }
        };

        checkTruncation();
        const resizeObserver = typeof ResizeObserver === "undefined"
            ? undefined
            : new ResizeObserver(checkTruncation);
        resizeObserver?.observe(element);
        window.addEventListener("resize", checkTruncation);
        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", checkTruncation);
        };
    }, [text, isExpanded]);

    const showFull = isExpanded;
    const expandable = isTruncated || isExpanded;
    const clickable = Boolean(onClick) || expandable;
    const tooltipTitle = tooltipContent ?? text;

    return (
        <Tooltip
            title={tooltipTitle}
            enterTouchDelay={tooltipContent ? 0 : undefined}
            arrow
        >
            <Box
                component="span"
                data-element-chip
                sx={{
                    display: "inline-flex",
                    alignItems: showFull ? "flex-start" : "center",
                    height: showFull ? "auto" : "28px",
                    py: showFull ? "4px" : 0,
                    maxWidth: "100%",
                    minWidth: 0,
                    borderRadius: "6px",
                    border: theme => `1px solid ${borderColor ?? theme.palette.divider}`,
                    backgroundColor: backgroundColor ?? "background.paper",
                    color: "text.primary",
                    boxShadow: "0 1px 1px rgba(15, 23, 42, 0.03)",
                    whiteSpace: showFull ? "normal" : "nowrap",
                    "@media print": {
                        pointerEvents: "none !important",
                        boxShadow: "none !important",
                        backgroundColor: "#fff !important",
                        color: "#1f2937 !important",
                        border: "1px solid #ccc !important",
                        height: "22px !important",
                        py: "0px !important",
                        alignItems: "center !important",
                        whiteSpace: "nowrap !important",
                    },
                }}
            >
                <Box
                    component="span"
                    data-element-chip-action
                    tabIndex={clickable ? 0 : undefined}
                    role={clickable ? "button" : undefined}
                    aria-expanded={!onClick && expandable ? isExpanded : undefined}
                    onClick={event => {
                        if (onClick) {
                            event.stopPropagation();
                            onClick();
                            return;
                        }

                        const element = textRef.current;
                        const canToggle = isExpanded
                            || (!!element && element.scrollWidth > element.clientWidth);
                        if (!canToggle) return;

                        event.stopPropagation();
                        setIsExpanded(current => !current);
                    }}
                    onKeyDown={event => {
                        if ((event.key !== "Enter" && event.key !== " ") || !clickable) return;

                        event.preventDefault();
                        event.stopPropagation();
                        if (onClick) {
                            onClick();
                        } else {
                            setIsExpanded(current => !current);
                        }
                    }}
                    sx={{
                        display: "inline-flex",
                        alignItems: showFull ? "flex-start" : "center",
                        gap: startIcon ? "5px" : 0,
                        flex: "1 1 auto",
                        minWidth: 0,
                        alignSelf: showFull ? "stretch" : "center",
                        pl: "8px",
                        pr: onDelete ? "3px" : "8px",
                        cursor: clickable ? "pointer" : "inherit",
                        userSelect: clickable ? "none" : "auto",
                        "@media print": {
                            pl: "6px !important",
                            pr: "6px !important",
                        },
                    }}
                >
                    {startIcon && (
                        <Box
                            component="span"
                            data-element-chip-icon
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                flex: "0 0 auto",
                                mt: showFull ? "2px" : 0,
                                "@media print": {
                                    mt: "0px !important",
                                    transform: "scale(0.85) !important",
                                },
                            }}
                        >
                            {startIcon}
                        </Box>
                    )}
                    <Box
                        ref={textRef}
                        component="span"
                        data-element-chip-label
                        sx={{
                            minWidth: 0,
                            overflow: showFull ? "visible" : "hidden",
                            textOverflow: showFull ? "clip" : "ellipsis",
                            whiteSpace: showFull ? "normal" : "nowrap",
                            wordBreak: showFull ? "break-word" : "normal",
                            lineHeight: "16px",
                            mt: "2px",
                            "@media print": {
                                fontSize: "11px !important",
                                lineHeight: "14px !important",
                                mt: "0px !important",
                                overflow: "hidden !important",
                                textOverflow: "ellipsis !important",
                                whiteSpace: "nowrap !important",
                            },
                        }}
                    >
                        {text}
                    </Box>
                </Box>
                {onDelete && (
                    <IconButton
                        size="small"
                        data-element-chip-delete
                        aria-label={`Delete ${text}`}
                        onPointerDown={event => event.stopPropagation()}
                        onClick={event => {
                            event.stopPropagation();
                            onDelete();
                        }}
                        sx={{
                            flex: "0 0 auto",
                            alignSelf: showFull ? "flex-start" : "center",
                            width: "20px",
                            height: "20px",
                            p: 0,
                            mr: "3px",
                            color: "text.secondary",
                            "&:hover": {
                                color: "error.main",
                                backgroundColor: "action.hover",
                            },
                            "@media print": {
                                display: "none !important",
                            },
                        }}
                    >
                        <CancelIcon sx={{fontSize: "16px"}}/>
                    </IconButton>
                )}
            </Box>
        </Tooltip>
    );
}
